import React, { useEffect, useState } from 'react';
import { StepsChartSC } from './steps-chart.styled';
import moment from 'moment';
import { ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, Line } from 'recharts';
import { IBehavioralTimelineData, TBehavioralModalType } from 'types/behavioral-data';
import { FormattedMessage, useIntl } from 'react-intl';
import HistoryStepper from '../../../../../components/history-stepper';
import StepsChartSummary from './steps-chart-summary';
import PageSwitcher from '../../../../../components/page-switcher';
import { ReactComponent as StepsIcon } from '../../../../../assets/svg/icon-steps.svg';
import { ReactComponent as ExclamIcon } from '../../../../../assets/svg/icon-exclam.svg';
import { SegmentedControl } from '@components/ui';

const TABS = [
  { label: 'life_id.vitalDetail.periods.day', value: 'day', key: 'day' },
  { label: 'life_id.vitalDetail.periods.week', value: 'week', key: 'week' },
  { label: 'life_id.vitalDetail.periods.month', value: 'month', key: 'month' },
  { label: 'life_id.vitalDetail.periods.year', value: 'year', key: 'year' },
];

const PAGES = [{ type: 'chart' }, { type: 'summary' }];

interface StepsChartProps {
  type: TBehavioralModalType;
  selectedPeriod: string;
  onPeriodChange: (value: string) => void;
  historyDate: string | string[];
  historyCount: number;
  onHistoryStepChange: (historyType: string) => void;
  isCustomTimeSelected: boolean;
  data: IBehavioralTimelineData | null;
}

const StepsChart: React.FC<StepsChartProps> = ({
  type,
  selectedPeriod,
  onPeriodChange,
  historyDate,
  historyCount,
  onHistoryStepChange,
  isCustomTimeSelected,
  data,
}) => {
  const { formatMessage } = useIntl();
  const [focusBar, setFocusBar] = useState<number | undefined>(undefined);
  const [formattedData, setFormattedData] = useState<any[]>([]);
  const [chartDataKeys, setChartDataKeys] = useState({ x: '', x2: '', y: '' });
  const [chartDesign, setChartDesign] = useState({
    barSize: 8,
    interval: 0,
  });
  const [switcherCurrentPage, setSwitcherCurrentPage] = useState<number>(0);
  const tabOpts = React.useMemo(
    () => TABS.map((o) => ({ ...o, label: formatMessage({ id: o.label }) })),
    [formatMessage],
  );

  useEffect(() => {
    if (data?.behavioralDataUnit === 'step') {
      setFormattedData([...data?.values]);
      setChartDataKeys({
        x: 'value',
        x2: 'goal',
        y: 'timestampTo',
      });
    }
  }, [data?.values]);

  useEffect(() => {
    if (selectedPeriod === 'week' || (formattedData.length > 4 && selectedPeriod === 'year')) {
      setChartDesign({
        barSize: 8,
        interval: 0,
      });
    }
    if (
      (formattedData.length <= 4 && selectedPeriod === 'month') ||
      (formattedData.length <= 4 && selectedPeriod === 'year') ||
      (formattedData.length <= 4 && selectedPeriod === 'day')
    ) {
      setChartDesign({
        barSize: 8,
        interval: 0,
      });
    }
    if (
      (formattedData.length > 4 && selectedPeriod === 'month') ||
      (formattedData.length > 4 && selectedPeriod === 'day')
    ) {
      setChartDesign({
        barSize: 6,
        interval: Math.round(formattedData.length - 2),
      });
    }
  }, [formattedData, selectedPeriod]);

  const CustomTooltip = (props: any) => {
    const { active, payload, period } = props;
    if (active && payload) {
      return (
        <div className="custom-tooltip">
          <p className="label">
            {moment(payload[0].payload.timestampTo).format(
              period === 'day' ? 'HH:mm:ss' : 'DD.MM.YYYY',
            )}
          </p>
          <p className="label">
            <FormattedMessage id="life-id.steps_count" />:{' '}
            {payload[0].value && Math.round(Number(payload[0].value)).toLocaleString()}
          </p>
          <p className="label">
            {period === 'year' ? (
              <FormattedMessage id="life-id.month_goal" />
            ) : period === 'day' ? (
              <FormattedMessage id="life-id.target" />
            ) : (
              <FormattedMessage id="life-id.day_goal" />
            )}
            : {`${payload[1].value && payload[1].value.toLocaleString()}`}
          </p>
        </div>
      );
    }

    return null;
  };

  const CustomizedTick = (props: any) => {
    const { x, y, payload, index, period } = props;
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={period === 'year' ? 2 : 15}
          dx={period === 'month' ? 12 : period === 'year' ? -15 : 0}
          fill="#666"
          transform={period === 'year' ? 'rotate(-90)' : ''}
        >
          <tspan
            textAnchor="middle"
            x="0"
            style={{ fontWeight: 800, fontSize: period === 'day' ? '11px' : '12px' }}
            fill="#FFFFFF"
          >
            {period === 'day' && moment(payload.value).format('HH:mm')}
            {period === 'week' && <>{index < 9 ? `0${index + 1}` : index + 1}</>}
            {period === 'month' && moment(payload.value).format('DD.MM')}
            {period === 'year' && moment(payload.value).format('MMM')}
          </tspan>
          {period === 'week' ? (
            <tspan
              textAnchor="middle"
              x="0"
              dy="18"
              style={{ fontWeight: 400, fontSize: '12px', opacity: 0.5 }}
              fill="#FFFFFF"
            >
              {moment(payload.value).format('ddd')}
            </tspan>
          ) : (
            ''
          )}
        </text>
      </g>
    );
  };

  return (
    <StepsChartSC>
      <div className="sc-header">
        <div className="info">
          <div className="meta">
            {type === 'STEPS' && (
              <>
                <StepsIcon />
                <FormattedMessage id="life-id.steps" />
              </>
            )}
          </div>
        </div>
        <div className="tabs">
          <SegmentedControl
            variant="on-dark"
            options={tabOpts}
            value={selectedPeriod}
            onChange={isCustomTimeSelected ? undefined : onPeriodChange}
          />
        </div>
        {isCustomTimeSelected ? (
          ''
        ) : (
          <HistoryStepper
            selectedPeriod={selectedPeriod}
            historyDate={historyDate}
            historyCount={historyCount}
            onHistoryStepChange={(historyType: string) => onHistoryStepChange(historyType)}
          />
        )}
      </div>
      {formattedData.length > 0 ? (
        <>
          {PAGES[switcherCurrentPage].type === 'chart' && (
            <>
              <ComposedChart
                key={formattedData.length}
                width={364}
                height={200}
                style={{ background: '#333D6C' }}
                data={formattedData}
                margin={{ top: 20, left: 0, right: 0, bottom: 20 }}
                onMouseMove={(state) => {
                  if (state.isTooltipActive) {
                    setFocusBar(state.activeTooltipIndex);
                  } else {
                    setFocusBar(undefined);
                  }
                }}
              >
                <XAxis
                  xAxisId={0}
                  dataKey={chartDataKeys.y}
                  tick={<CustomizedTick period={selectedPeriod} />}
                  stroke="#ccc"
                  strokeWidth={0.5}
                  interval={chartDesign.interval}
                  domain={['dataMin', 'dataMax']}
                />
                <XAxis xAxisId={1} hide domain={['dataMin', 'dataMax']} />
                <YAxis
                  orientation="right"
                  stroke="#fff"
                  style={{ fontSize: '12px', fontWeight: 700 }}
                  tickFormatter={(val) => `${val.toLocaleString()}`}
                  strokeWidth={0.5}
                />
                <Tooltip cursor={false} content={<CustomTooltip period={selectedPeriod} />} />

                <CartesianGrid
                  horizontal={true}
                  vertical={false}
                  stroke="#ccc"
                  strokeDasharray="3 3"
                  strokeWidth={0.5}
                />

                <Bar
                  dataKey={chartDataKeys.x}
                  xAxisId={1}
                  radius={[10, 10, 0, 0]}
                  barSize={chartDesign.barSize}
                >
                  {formattedData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill="#6AD5E5"
                      opacity={focusBar === index ? 1 : 0.7}
                    />
                  ))}
                </Bar>
                <Line
                  type="monotone"
                  dataKey={chartDataKeys.x2}
                  stroke="#FFFFFF"
                  strokeDasharray="5 5"
                  activeDot={false}
                  dot={false}
                />
                <Bar
                  dataKey={chartDataKeys.x2}
                  xAxisId={0}
                  barSize={chartDesign.barSize}
                  maxBarSize={8}
                >
                  {formattedData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill="#FFFFFF"
                      opacity={focusBar === index ? 1 : 0.7}
                    />
                  ))}
                </Bar>
              </ComposedChart>

              {selectedPeriod !== 'day' && (
                <div className="chart-legend">
                  <div className="chart-legend__item">
                    <span className="circle grey"></span>
                    {selectedPeriod === 'year' ? (
                      <FormattedMessage id="life-id.month_goal" />
                    ) : (
                      <FormattedMessage id="life-id.day_goal" />
                    )}
                  </div>
                  <div className="chart-legend__item">
                    <span className="circle blue"></span>
                    {selectedPeriod === 'year' ? (
                      <FormattedMessage id="life-id.month_above_goal" />
                    ) : (
                      <FormattedMessage id="life-id.day_above_goal" />
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          {PAGES[switcherCurrentPage].type === 'summary' && (
            <StepsChartSummary data={formattedData} selectedPeriod={selectedPeriod} />
          )}
          <PageSwitcher
            pages={PAGES}
            switcherCurrentPage={switcherCurrentPage}
            onSwitcherPageChange={(p) => setSwitcherCurrentPage(p)}
          />
        </>
      ) : (
        <div className="no-data">
          <ExclamIcon />
          <FormattedMessage id="life-id.no_data" />
        </div>
      )}
    </StepsChartSC>
  );
};

export default StepsChart;
